import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
} from "lucide-react";
import {
  API,
  fmtDate,
} from "./shared";

export function AuditTab({ authHeader }) {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: "", actor: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/audit-logs`, {
          headers: authHeader(),
          params: { action: filters.action || undefined, actor: filters.actor || undefined },
        }),
        axios.get(`${API}/admin/audit-logs/summary`, { headers: authHeader() }),
      ]);
      setLogs(l.data);
      setSummary(s.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inputCls =
    "bg-white border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div data-testid="audit-tab">
      <div className="mb-6 max-w-xl">
        <div className="font-serif text-2xl text-[#3F5043] mb-1">Audit Log</div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed">
          Security trail of sign-ins and sensitive changes — who did what, and when.
        </p>
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        <input placeholder="Filter by action (e.g. staff_login)" value={filters.action}
          data-testid="audit-filter-action"
          onChange={(e) => setFilters({ ...filters, action: e.target.value })} className={inputCls} />
        <input placeholder="Filter by person (email)" value={filters.actor}
          data-testid="audit-filter-actor"
          onChange={(e) => setFilters({ ...filters, actor: e.target.value })} className={inputCls} />
        <button onClick={load} data-testid="audit-apply"
          className="text-xs uppercase tracking-[0.15em] font-semibold rounded-full px-5 py-2 bg-[#3F5043] text-[#F8F7F3] hover:bg-[#875F45] transition-colors">
          Apply
        </button>
      </div>
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="audit-summary">
          {[
            ["Events (30d)", summary.total_events],
            ["People active", summary.unique_actors],
            ["Failed sign-ins", summary.failed_logins],
            ["Action types", Object.keys(summary.by_action).length],
          ].map(([l, v]) => (
            <div key={l} className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 border-l-4 border-l-[#3F5043]">
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#899480] font-semibold mb-1">{l}</div>
              <div className="font-serif text-2xl text-[#3F5043]">{v}</div>
            </div>
          ))}
        </div>
      )}
      {loading ? (
        <div className="text-center py-10"><Loader2 size={18} className="animate-spin inline text-[#899480]" /></div>
      ) : logs.length === 0 ? (
        <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-10 text-center text-sm text-[#899480]" data-testid="audit-empty">
          No audit events yet.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-[#EDEDEE] text-left text-[10px] uppercase tracking-[0.15em] text-[#899480]">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Who</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Target</th>
              </tr>
            </thead>
            <tbody data-testid="audit-table">
              {logs.map((l) => (
                <tr key={l.id} data-testid={`audit-row-${l.id}`} className="border-t border-[#BCB3A6]/20">
                  <td className="px-4 py-2.5 text-[#3F5043]/70 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  <td className="px-4 py-2.5 text-[#3F5043]">{l.actor}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] uppercase tracking-[0.1em] font-semibold bg-[#3F5043]/10 text-[#3F5043] rounded-full px-2 py-0.5">
                      {l.action}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-[#3F5043]/70">{l.target || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
